import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Gauge, RefreshCw, ShieldOff, ShieldCheck, SlidersHorizontal, X } from 'lucide-react';
import { vendorSlaService } from '../services/vendorSla.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useDialog } from '../contexts/DialogContext';

const BANDS = ['EXCELLENT', 'STRONG', 'STANDARD', 'WATCH', 'POOR'];

const bandClass = (band, suspended) => {
    if (suspended) return 'bg-red-100 text-red-700 border-red-300';
    switch (band) {
        case 'EXCELLENT':
        case 'STRONG':
            return 'bg-green-100 text-green-700 border-green-300';
        case 'WATCH':
            return 'bg-amber-100 text-amber-800 border-amber-300';
        case 'POOR':
            return 'bg-red-100 text-red-700 border-red-300';
        default:
            return 'bg-gray-100 text-gray-600 border-gray-300';
    }
};

const pct = (v) => (v == null ? 0 : Math.round(v * 100));

const Bar = ({ value, label }) => (
    <div className="flex items-center gap-1" title={`${label}: ${value == null ? '—' : pct(value) + '%'}`}>
        <span className="text-[10px] text-gray-400 w-3">{label}</span>
        <span className="inline-block w-14 h-1.5 rounded bg-gray-200 overflow-hidden">
            <span
                className={`block h-full ${value == null ? 'bg-gray-300' : value >= 0.75 ? 'bg-green-500' : value >= 0.5 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${value == null ? 100 : pct(value)}%` }}
            />
        </span>
    </div>
);

const Spark = ({ points }) => {
    const vals = (points || []).map((p) => p.sla_score).filter((v) => v != null);
    if (vals.length < 2) return <span className="text-xs text-gray-400">—</span>;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const rng = max - min || 1;
    const w = 120;
    const h = 28;
    const d = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - min) / rng) * h}`).join(' ');
    return (
        <svg width={w} height={h} className="overflow-visible">
            <polyline points={d} fill="none" stroke="#2563eb" strokeWidth="1.5" />
            <circle cx={w} cy={h - ((vals[vals.length - 1] - min) / rng) * h} r="2" fill="#2563eb" />
        </svg>
    );
};

const money = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);

const VendorPerformance = () => {
    const { prompt } = useDialog();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [filters, setFilters] = useState({ band: '', suspended: '' });
    const [detail, setDetail] = useState(null);
    const [detailBusy, setDetailBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.band) params.band = filters.band;
            if (filters.suspended !== '') params.suspended = filters.suspended === 'yes';
            const res = await vendorSlaService.leaderboard(params);
            setRows(Array.isArray(res?.items) ? res.items : []);
        } catch (e) {
            setMessage(e.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (vendorId) => {
        setDetailBusy(true);
        setDetail({ vendor_id: vendorId });
        try {
            setDetail(await vendorSlaService.detail(vendorId));
        } catch (e) {
            setMessage(e.message || 'Failed to load vendor');
            setDetail(null);
        } finally {
            setDetailBusy(false);
        }
    };

    const act = async (fn, okMsg) => {
        try {
            await fn();
            setMessage(okMsg);
            await load();
            if (detail?.performance?.vendor_id) {
                setDetail(await vendorSlaService.detail(detail.performance.vendor_id));
            }
        } catch (e) {
            setMessage(e.message || 'Action failed');
        }
    };

    const onSuspend = async (vendorId) => {
        const values = await prompt({
            title: 'Suspend vendor',
            confirmLabel: 'Suspend',
            danger: true,
            fields: [
                { key: 'reason', label: 'Suspension reason', required: true, multiline: true },
                { key: 'until', label: 'Reinstate on (YYYY-MM-DD)', placeholder: 'Leave blank for indefinite' },
            ],
        });
        if (!values) return;
        act(
            () => vendorSlaService.suspend(vendorId, { reason: values.reason, until: values.until || null }),
            'Vendor suspended',
        );
    };

    const onOverride = async (vendorId) => {
        const values = await prompt({
            title: 'Pin SLA band',
            confirmLabel: 'Save',
            fields: [
                { key: 'band', label: `Band (${BANDS.join(' / ')})`, placeholder: 'Leave blank to clear' },
                { key: 'reason', label: 'Override reason', placeholder: 'Required when pinning a band' },
                { key: 'until', label: 'Until (YYYY-MM-DD)', placeholder: 'Leave blank for indefinite' },
            ],
        });
        if (!values) return;
        const band = (values.band || '').trim();
        if (band && !BANDS.includes(band.toUpperCase())) {
            setMessage(`Band must be one of ${BANDS.join(', ')}`);
            return;
        }
        act(
            () => vendorSlaService.override(vendorId, {
                band: band ? band.toUpperCase() : null,
                reason: band ? (values.reason || null) : null,
                until: band ? (values.until || null) : null,
            }),
            band ? 'Band pinned' : 'Override cleared',
        );
    };

    const summary = useMemo(() => {
        const scored = rows.filter((r) => r.sla_score != null);
        const suspended = rows.filter((r) => r.is_suspended).length;
        const avg = scored.length ? Math.round(scored.reduce((a, r) => a + r.sla_score, 0) / scored.length) : null;
        return { total: rows.length, scored: scored.length, suspended, avg };
    }, [rows]);

    return (
        <div className="p-4 h-full">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5" />
                    <h1 className="text-xl font-semibold">Vendor Performance</h1>
                    {summary.avg != null && (
                        <span className="text-xs text-gray-500">
                            {summary.scored}/{summary.total} scored · avg SLA {summary.avg} · {summary.suspended} suspended
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="px-3 py-1.5 border rounded text-sm"
                        onClick={() => act(() => vendorSlaService.recompute(), 'Recompute queued')}
                    >
                        <RefreshCw className="w-4 h-4 inline mr-1" />Recompute all
                    </button>
                </div>
            </div>

            <div className="flex gap-3 mb-3 text-sm">
                <select
                    className="border rounded px-2 py-1"
                    value={filters.band}
                    onChange={(e) => setFilters((f) => ({ ...f, band: e.target.value }))}
                >
                    <option value="">All bands</option>
                    {BANDS.map((b) => (
                        <option key={b} value={b}>{b}</option>
                    ))}
                </select>
                <select
                    className="border rounded px-2 py-1"
                    value={filters.suspended}
                    onChange={(e) => setFilters((f) => ({ ...f, suspended: e.target.value }))}
                >
                    <option value="">All</option>
                    <option value="yes">Suspended only</option>
                    <option value="no">Not suspended</option>
                </select>
                {message && <span className="self-center text-gray-600">{message}</span>}
            </div>

            <div className="border rounded bg-white overflow-auto" style={{ maxHeight: 'calc(100vh - 190px)' }}>
                {loading && (
                    <div className="p-8 flex justify-center">
                        <LoadingSpinner />
                    </div>
                )}
                {!loading && (
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-500 bg-gray-50 sticky top-0">
                            <tr>
                                <th className="text-left px-3 py-2">Vendor</th>
                                <th className="text-left px-3 py-2">Band</th>
                                <th className="text-right px-3 py-2">SLA</th>
                                <th className="text-left px-3 py-2">Components</th>
                                <th className="text-right px-3 py-2">Jobs</th>
                                <th className="text-right px-3 py-2">Accept</th>
                                <th className="text-right px-3 py-2">Rating</th>
                                <th className="text-right px-3 py-2">Revenue</th>
                                <th className="text-right px-3 py-2">Commission</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr
                                    key={r.vendor_id}
                                    className="border-t hover:bg-gray-50 cursor-pointer"
                                    onClick={() => openDetail(r.vendor_id)}
                                >
                                    <td className="px-3 py-2">
                                        <div className="font-medium">{r.vendor_name}</div>
                                        <div className="text-[11px] text-gray-400">{r.vendor_code}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${bandClass(r.band, r.is_suspended)}`}>
                                            {r.is_suspended ? 'SUSPENDED' : r.band || '—'}
                                            {r.is_provisional && !r.is_suspended ? ' · prov' : ''}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                                        {r.sla_score == null ? '—' : r.sla_score.toFixed(0)}
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.components ? (
                                            <div className="flex flex-col gap-0.5">
                                                <Bar label="A" value={r.components.acceptance} />
                                                <Bar label="R" value={r.components.arrival} />
                                                <Bar label="C" value={r.components.completion} />
                                                <Bar label="G" value={r.components.rating} />
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-xs">no data</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{r.jobs_in_window}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {r.acceptance_rate == null ? '—' : `${pct(r.acceptance_rate)}%`}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {r.avg_rating == null ? '—' : r.avg_rating.toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{money(r.revenue_window)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{money(r.commission_window)}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                        {r.is_suspended ? (
                                            <button
                                                type="button"
                                                className="text-xs text-green-700 hover:underline"
                                                onClick={() => act(() => vendorSlaService.reinstate(r.vendor_id), 'Vendor reinstated')}
                                            >
                                                <ShieldCheck className="w-3.5 h-3.5 inline" /> Reinstate
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="text-xs text-red-600 hover:underline"
                                                onClick={() => onSuspend(r.vendor_id)}
                                            >
                                                <ShieldOff className="w-3.5 h-3.5 inline" /> Suspend
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr>
                                    <td colSpan={10} className="px-3 py-8 text-center text-gray-400">
                                        No vendors match.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {detail && (
                <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white border-l shadow-xl z-30 flex flex-col">
                    <div className="flex items-center justify-between p-3 border-b">
                        <span className="font-semibold">
                            {detail.performance?.vendor_name || 'Vendor'} · {detail.performance?.vendor_code || ''}
                        </span>
                        <button type="button" onClick={() => setDetail(null)}>
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-auto p-4 space-y-4 text-sm">
                        {detailBusy && <LoadingSpinner />}
                        {!detailBusy && detail.performance && (
                            <>
                                <div className="flex items-center gap-3">
                                    <span className="text-3xl font-semibold tabular-nums">
                                        {detail.performance.sla_score == null ? '—' : detail.performance.sla_score.toFixed(0)}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full border ${bandClass(detail.performance.band, detail.performance.is_suspended)}`}>
                                        {detail.performance.is_suspended ? 'SUSPENDED' : detail.performance.band || '—'}
                                    </span>
                                    {detail.performance.override_band && (
                                        <span className="text-[11px] text-blue-700">pinned: {detail.performance.override_band}</span>
                                    )}
                                    <span className="ml-auto"><Spark points={detail.history} /></span>
                                </div>

                                {detail.performance.is_suspended && detail.performance.suspended_reason && (
                                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                                        Suspended — {detail.performance.suspended_reason}
                                        {detail.performance.suspended_until ? ` (until ${detail.performance.suspended_until.slice(0, 10)})` : ''}
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        ['Acceptance', detail.performance.components?.acceptance, `rate ${detail.performance.acceptance_rate == null ? '—' : pct(detail.performance.acceptance_rate) + '%'} · ${detail.performance.avg_response_time_sec ?? '—'}s`],
                                        ['Arrival', detail.performance.components?.arrival, `${detail.performance.jobs_in_window} jobs`],
                                        ['Completion', detail.performance.components?.completion, ''],
                                        ['Rating', detail.performance.components?.rating, detail.performance.avg_rating == null ? '' : `avg ${detail.performance.avg_rating.toFixed(2)}`],
                                    ].map(([label, val, sub]) => (
                                        <div key={label} className="border rounded p-2">
                                            <div className="text-[11px] uppercase text-gray-500">{label}</div>
                                            <div className="text-lg tabular-nums">{val == null ? '—' : `${pct(val)}%`}</div>
                                            {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-4 text-xs text-gray-600">
                                    <span>Revenue {money(detail.performance.revenue_window)}</span>
                                    <span>Commission {money(detail.performance.commission_window)}</span>
                                    <span>Offers {detail.performance.offers_in_window}</span>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        className="text-xs border rounded px-2 py-1"
                                        onClick={() => act(() => vendorSlaService.recompute(detail.performance.vendor_id), 'Recomputed')}
                                    >
                                        <RefreshCw className="w-3.5 h-3.5 inline mr-1" />Recompute
                                    </button>
                                    <button
                                        type="button"
                                        className="text-xs border rounded px-2 py-1"
                                        onClick={() => onOverride(detail.performance.vendor_id)}
                                    >
                                        <SlidersHorizontal className="w-3.5 h-3.5 inline mr-1" />Override band
                                    </button>
                                    {detail.performance.is_suspended ? (
                                        <button
                                            type="button"
                                            className="text-xs border rounded px-2 py-1 text-green-700"
                                            onClick={() => act(() => vendorSlaService.reinstate(detail.performance.vendor_id), 'Reinstated')}
                                        >
                                            Reinstate
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="text-xs border rounded px-2 py-1 text-red-600"
                                            onClick={() => onSuspend(detail.performance.vendor_id)}
                                        >
                                            Suspend
                                        </button>
                                    )}
                                </div>

                                <div>
                                    <p className="text-[11px] uppercase text-gray-500 mb-1">Recent jobs</p>
                                    <div className="border rounded overflow-auto">
                                        <table className="w-full text-xs">
                                            <thead className="text-gray-400 bg-gray-50">
                                                <tr>
                                                    <th className="text-left px-2 py-1">Completed</th>
                                                    <th className="text-left px-2 py-1">Type</th>
                                                    <th className="text-right px-2 py-1">Accept</th>
                                                    <th className="text-right px-2 py-1">ETA/act</th>
                                                    <th className="text-right px-2 py-1">Work</th>
                                                    <th className="text-right px-2 py-1">Rate</th>
                                                    <th className="text-right px-2 py-1">SLA</th>
                                                    <th className="text-right px-2 py-1">Rev</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(detail.recent_jobs || []).map((j) => (
                                                    <tr key={j.service_id} className={`border-t ${j.excluded_from_sla ? 'opacity-40' : ''}`}>
                                                        <td className="px-2 py-1">{j.completed_at ? j.completed_at.slice(0, 10) : '—'}</td>
                                                        <td className="px-2 py-1">{j.service_type_code || '—'}</td>
                                                        <td className="px-2 py-1 text-right tabular-nums">{j.response_time_sec == null ? '—' : `${j.response_time_sec}s`}</td>
                                                        <td className="px-2 py-1 text-right tabular-nums">
                                                            {j.eta_minutes == null ? '—' : `${j.eta_minutes}/${j.travel_minutes ?? '—'}m`}
                                                        </td>
                                                        <td className="px-2 py-1 text-right tabular-nums">{j.work_minutes == null ? '—' : `${j.work_minutes}m`}</td>
                                                        <td className="px-2 py-1 text-right tabular-nums">{j.overall_rating ?? '—'}</td>
                                                        <td className="px-2 py-1 text-right tabular-nums">{j.sla_score == null ? '—' : j.sla_score.toFixed(0)}</td>
                                                        <td className="px-2 py-1 text-right tabular-nums">{money(j.revenue_amount)}</td>
                                                    </tr>
                                                ))}
                                                {!(detail.recent_jobs || []).length && (
                                                    <tr>
                                                        <td colSpan={8} className="px-2 py-3 text-center text-gray-400">No jobs yet.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorPerformance;
